var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useFrame, useThree } from "@react-three/fiber";
import React, { useEffect, useRef, useState } from "react";
import { Euler, Quaternion, Vector3, } from "three";
import { lerp } from "three/src/math/MathUtils";
import { clamp } from "lodash";
import { CameraAtom, CameraTarget, useMoveCamera } from "../atoms";
import { useAtomValue } from "jotai";
import { useAtom } from "jotai";
import { useArrowsControls } from "@/utils/arrowsControls";
import { easeInOutCubic } from "../utils/easing";
import { CONFIG } from "../config";
var DegToRad = (Math.PI * 2) / 360;
export var MIN_ZOOM = -0.2;
var CameraControl2 = /** @class */ (function () {
    function CameraControl2(holder, camera, domElement, cameraAnimation, target, onTargetChange) {
        this.holder = holder;
        this.camera = camera;
        this.domElement = domElement;
        this.onTargetChange = onTargetChange;
        // TODO: extract to config, or compute automatically
        this.contentWidth = 10;
        this.contentHeight = 6;
        this.minDistance = 4;
        this.target = {
            hPos: 0,
            vPos: 0,
            zoom: 0,
        };
        this.initialCameraPos = {
            rotation: 0,
            y: 0,
            z: 0,
        };
        this.controlsEnabled = false;
        this.lastUpdate = Date.now();
        this.introAnimationPlaying = true;
        camera.fov = 28;
        camera.near = 0.1;
        camera.far = 100;
        holder.rotation.set(-Math.PI / 2, 0, 0);
        this.initialCameraPos.rotation = camera.rotation.x;
        this.initialCameraPos.y = camera.position.y;
        this.initialCameraPos.z = camera.position.z;
        camera.clearViewOffset();
        this.setTarget(target, true);
        this.update(cameraAnimation);
    }
    CameraControl2.prototype.getCurrentZoom = function () {
        return ((this.holder.position.y - this.getMaxDistance()) /
            (this.minDistance - this.getMaxDistance()));
    };
    CameraControl2.prototype.setTarget = function (t, force) {
        if (force === void 0) { force = false; }
        if (!this.controlsEnabled && !force)
            return;
        this.target.zoom = clamp(t.zoom, MIN_ZOOM, 1);
        var hMovement = Math.max(0, this.contentWidth - this.getDisplayedWidthAtZoom(this.target.zoom));
        var vMovement = Math.max(0, this.contentHeight - this.getDisplayedHeightAtZoom(this.target.zoom));
        this.target.hPos = clamp(t.hPos, -hMovement / 2, hMovement / 2);
        this.target.vPos = clamp(t.vPos, -vMovement / 2, vMovement / 2);
        if (this.target.zoom !== t.zoom ||
            this.target.hPos !== t.hPos ||
            this.target.vPos !== t.vPos) {
            this.onTargetChange(__assign({}, this.target));
        }
    };
    CameraControl2.prototype.setPerspective = function (zoom) {
        var rot = this.getPerspectiveRotation(zoom);
        var newRot = lerp(this.camera.rotation.x, rot, 0.2);
        var newLocalPos = this.getPerspectivePosition(newRot);
        this.camera.rotation.x = newRot;
        this.camera.position.y = newLocalPos.y;
        this.camera.position.z = newLocalPos.z;
    };
    CameraControl2.prototype.getPerspectiveRotation = function (zoom) {
        return convertRangeClamped(zoom, [MIN_ZOOM, 0.2], [Math.PI / 6, 0]);
    };
    CameraControl2.prototype.getPerspectivePosition = function (rotation) {
        var holderWorldPos = this.holder.getWorldPosition(new Vector3());
        var targetWorldY = Math.cos(-rotation) * holderWorldPos.y;
        var targetWorldZ = -Math.sin(-rotation) * holderWorldPos.y + this.holder.position.z;
        return this.holder.worldToLocal(new Vector3(0, targetWorldY, targetWorldZ));
    };
    CameraControl2.prototype.zoomToZ = function (zoom) {
        return lerp(this.getMaxDistance(), this.minDistance, zoom);
    };
    CameraControl2.prototype.getVFov = function () {
        return this.camera.fov * DegToRad;
    };
    CameraControl2.prototype.getHFov = function () {
        return Math.atan(Math.tan(this.getVFov() / 2) * this.camera.aspect) * 2;
    };
    CameraControl2.prototype.getMaxDistance = function () {
        var distanceToFitWidth = this.contentWidth / 2 / Math.tan(this.getHFov() / 2);
        var distanceToFitHeight = this.contentHeight / 2 / Math.tan(this.getVFov() / 2);
        var res = Math.max(distanceToFitWidth, distanceToFitHeight);
        if (isNaN(res)) {
            return 1000;
        }
        else {
            return res;
        }
    };
    CameraControl2.prototype.getDisplayedHeight = function () {
        return this.holder.position.y * Math.tan(this.getVFov() / 2) * 2;
    };
    CameraControl2.prototype.getDisplayedWidth = function () {
        return this.holder.position.y * Math.tan(this.getHFov() / 2) * 2;
    };
    CameraControl2.prototype.getDisplayedHeightAtZoom = function (zoom) {
        return this.zoomToZ(zoom) * Math.tan(this.getVFov() / 2) * 2;
    };
    CameraControl2.prototype.getDisplayedWidthAtZoom = function (zoom) {
        return this.zoomToZ(zoom) * Math.tan(this.getHFov() / 2) * 2;
    };
    CameraControl2.prototype.update = function (cameraAnimation) {
        this.controlsEnabled = cameraAnimation.intro.progress >= 1;
        var maxDistance = this.getMaxDistance();
        var now = Date.now();
        var dt = now - this.lastUpdate;
        this.lastUpdate = now;
        if (this.introAnimationPlaying) {
            var lerpRatio = dt > 1000 ? 1 : 0.2;
            var targetY = lerp(maxDistance, this.minDistance, lerp(this.getCurrentZoom(), this.target.zoom, lerpRatio));
            var targetX = lerp(this.holder.position.x, this.target.hPos, lerpRatio);
            var targetZ = lerp(this.holder.position.z, this.target.vPos, lerpRatio);
            var p = easeInOutCubic(
            // TODO: refacto timing config
            CONFIG.skipIntroAnimation
                ? convertRangeClamped(cameraAnimation.intro.progress, [0.0, 1], [0.99, 1])
                : convertRangeClamped(cameraAnimation.intro.progress, [0.5, 1], [0, 1]));
            this.holder.position.set(lerp(cameraAnimation.position.x, targetX, p), lerp(cameraAnimation.position.y, targetY, p), lerp(cameraAnimation.position.z, targetZ, p));
            var wp = this.holder.getWorldPosition(new Vector3());
            var lookAtAngle = Math.atan2(wp.y, wp.z);
            var targeLookAtRotation = new Quaternion().setFromEuler(new Euler(-lookAtAngle, 0, 0, "XYZ"));
            this.holder.rotation.setFromQuaternion(cameraAnimation.rotation.clone().slerp(targeLookAtRotation, p));
            if (p >= 1 &&
                Math.abs(this.holder.position.x - this.target.hPos) < 0.001 &&
                Math.abs(this.holder.position.y - targetY) < 0.001 &&
                Math.abs(this.holder.position.z - this.target.vPos) < 0.001) {
                this.holder.rotation.setFromQuaternion(targeLookAtRotation);
                this.introAnimationPlaying = false;
            }
        }
        else {
            var lerpRatio = 0.2;
            var targetY = lerp(maxDistance, this.minDistance, lerp(this.getCurrentZoom(), this.target.zoom, lerpRatio));
            var targetX = lerp(this.holder.position.x, this.target.hPos, lerpRatio);
            var targetZ = lerp(this.holder.position.z, this.target.vPos, lerpRatio);
            this.holder.position.set(targetX, targetY, targetZ);
        }
    };
    CameraControl2.prototype.translate = function (x, y) {
        if (!this.controlsEnabled)
            return;
        var displayedHeight = this.getDisplayedHeight();
        var ratio = displayedHeight / this.domElement.clientHeight;
        this.target.hPos -= x * ratio;
        this.target.vPos -= y * ratio;
        var hMovement = Math.max(0, this.contentWidth - this.getDisplayedWidthAtZoom(this.target.zoom));
        var vMovement = Math.max(0, this.contentHeight - this.getDisplayedHeightAtZoom(this.target.zoom));
        this.target.hPos = clamp(this.target.hPos, -hMovement / 2, hMovement / 2);
        this.target.vPos = clamp(this.target.vPos, -vMovement / 2, vMovement / 2);
        this.onTargetChange(__assign({}, this.target));
    };
    CameraControl2.prototype.zoom = function (z, x, y) {
        if (!this.controlsEnabled)
            return;
        this.target.zoom = clamp(this.target.zoom - z * 0.001, MIN_ZOOM, 1);
        var currentWidth = this.getDisplayedWidth();
        var currentHeight = this.getDisplayedHeight();
        var pX = convertRange(x, [0, this.domElement.clientWidth], [
            this.holder.position.x - currentWidth / 2,
            this.holder.position.x + currentWidth / 2,
        ]);
        var pY = convertRange(y, [0, this.domElement.clientHeight], [
            this.holder.position.z - currentHeight / 2,
            this.holder.position.z + currentHeight / 2,
        ]);
        var targetHeight = this.getDisplayedHeightAtZoom(this.target.zoom);
        var targetWidth = this.getDisplayedWidthAtZoom(this.target.zoom);
        this.target.hPos = calculateCenter(this.holder.position.x - currentWidth / 2, currentWidth, targetWidth, pX);
        this.target.vPos = calculateCenter(this.holder.position.z - currentHeight / 2, currentHeight, targetHeight, pY);
        this.onTargetChange(__assign({}, this.target));
    };
    CameraControl2.prototype.init = function () {
        var _this = this;
        var mouseDown = false;
        var movement = 0;
        function onPointerDown() {
            mouseDown = true;
            movement = 0;
        }
        function onPointerCancel() {
            mouseDown = false;
        }
        function onPointerUp() {
            mouseDown = false;
        }
        function onClick(e) {
            if (movement > 20) {
                e.preventDefault();
                e.stopPropagation();
            }
        }
        var onMouseWheel = function (e) {
            _this.zoom(e.deltaY, e.offsetX, e.offsetY);
        };
        var onPointerMove = function (e) {
            if (mouseDown) {
                movement += Math.sqrt(e.movementX * e.movementX + e.movementY * e.movementY);
                _this.translate(e.movementX, e.movementY);
            }
        };
        this.domElement.addEventListener("click", onClick, { capture: true });
        this.domElement.addEventListener("pointerdown", onPointerDown);
        window.addEventListener("pointercancel", onPointerCancel);
        this.domElement.addEventListener("wheel", onMouseWheel);
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp, {
            capture: true,
        });
        return function () {
            _this.domElement.removeEventListener("click", onClick, { capture: true });
            _this.domElement.removeEventListener("pointerdown", onPointerDown);
            window.removeEventListener("pointercancel", onPointerCancel);
            _this.domElement.removeEventListener("wheel", onMouseWheel);
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
        };
    };
    return CameraControl2;
}());
export { CameraControl2 };
export function CameraControl(_a) {
    var perspective = _a.perspective;
    var cameraAnimation = useAtomValue(CameraAtom);
    var _b = __read(useAtom(CameraTarget), 2), target = _b[0], setTarget = _b[1];
    var holderRef = useRef();
    var cameraRef = useRef();
    var set = useThree(function (s) { return s.set; });
    var _c = __read(useState(null), 2), controls = _c[0], setControls = _c[1];
    var domElement = useThree(function (s) { return s.gl.domElement; });
    useEffect(function () {
        if (cameraRef.current == null ||
            holderRef.current == null ||
            controls !== null) {
            return;
        }
        set({ camera: cameraRef.current });
        var cameraControls = new CameraControl2(holderRef.current, cameraRef.current, domElement, cameraAnimation, {
            hPos: CONFIG.models.initialCameraPosition.x,
            vPos: CONFIG.models.initialCameraPosition.y,
            zoom: CONFIG.models.initialCameraPosition.zoom,
        }, function (z) { return setTarget(z); });
        cameraControls.init();
        setControls(cameraControls);
    }, [cameraAnimation, controls, domElement, set, setTarget]);
    useEffect(function () {
        if (controls != null) {
            controls.setTarget(target);
        }
    }, [controls, target]);
    useFrame(function () {
        if (cameraRef.current) {
            cameraRef.current.updateProjectionMatrix();
        }
        var holder = holderRef.current;
        if (controls != null && holder != null) {
            controls.update(cameraAnimation);
            if (perspective) {
                var zoom = controls.getCurrentZoom();
                controls.setPerspective(zoom);
            }
        }
    });
    // Keyboard controls
    var moveCamera = useMoveCamera();
    useArrowsControls(moveCamera);
    return (React.createElement("group", { ref: holderRef },
        React.createElement("perspectiveCamera", { ref: cameraRef, aspect: domElement.clientWidth / domElement.clientHeight })));
}
function calculateRangeStart(fromStart, fromLength, toLength, staticPoint) {
    return staticPoint - ((staticPoint - fromStart) / fromLength) * toLength;
}
function calculateCenter(fromStart, fromLength, toLength, staticPoint) {
    var toStart = calculateRangeStart(fromStart, fromLength, toLength, staticPoint);
    return toStart + toLength / 2;
}
export function convertRange(n, fromRange, toRange) {
    var p = (n - fromRange[0]) / (fromRange[1] - fromRange[0]);
    return toRange[0] + p * (toRange[1] - toRange[0]);
}
export function convertRangeClamped(n, fromRange, toRange) {
    return clamp(convertRange(n, fromRange, toRange), Math.min.apply(Math, __spreadArray([], __read(toRange), false)), Math.max.apply(Math, __spreadArray([], __read(toRange), false)));
}
